// Subtle, blue-forward color coding for State codes and Specialties.
// Every state/specialty gets a deterministic tone from a harmonious
// teal -> blue -> indigo gradient range (no rainbow / gaudy colors),
// differentiated by hue position plus gentle lightness/saturation steps.
// The same tones are shared between Business Tracker badges and the
// Dashboard charts (which render them as soft vertical/horizontal gradients).

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
  "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC", "PR",
  "VI", "GU",
];

// Blue-family gradient range: teal/cyan (178) through blue to indigo (262)
const HUE_MIN = 178;
const HUE_MAX = 262;
const LIGHTNESS_STEPS = [45, 52, 59];
const SATURATION_STEPS = [42, 52];
const GOLDEN = 0.61803398875;

const hashString = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return h;
};

const toneFromPosition = (p, alt) => ({
  hue: Math.round(HUE_MIN + p * (HUE_MAX - HUE_MIN)),
  // ">>> 0" / ">>> 2" keep the hash unsigned; a signed shift on large 32-bit
  // hashes goes negative and would index outside the palette arrays.
  sat: SATURATION_STEPS[((alt >>> 2) % SATURATION_STEPS.length + SATURATION_STEPS.length) % SATURATION_STEPS.length],
  light: LIGHTNESS_STEPS[((alt >>> 0) % LIGHTNESS_STEPS.length + LIGHTNESS_STEPS.length) % LIGHTNESS_STEPS.length],
});

/** Split a comma-separated multi-value ("AR, MO") into a clean list. */
export const splitMulti = (value) =>
  (value || "").split(",").map((s) => s.trim()).filter(Boolean);

export const stateTone = (code) => {
  const idx = US_STATES.indexOf((code || "").toUpperCase());
  if (idx === -1) return null; // unknown token -> neutral grey
  return toneFromPosition((idx * GOLDEN) % 1, idx);
};

export const specialtyTone = (name) => {
  const clean = (name || "").trim().toLowerCase();
  if (!clean || clean === "n/a" || clean === "-") return null;
  const h = hashString(clean);
  return toneFromPosition((h % 997) / 997, h);
};

/** Inline style for a colored badge (soft tinted background, deep readable text). */
export const badgeStyle = (tone) =>
  tone == null
    ? { backgroundColor: "hsl(215 18% 95%)", color: "hsl(215 15% 38%)", borderColor: "hsl(215 15% 84%)" }
    : {
        backgroundColor: `hsl(${tone.hue} ${Math.max(tone.sat - 6, 30)}% 94%)`,
        color: `hsl(${tone.hue} ${tone.sat}% ${Math.max(tone.light - 16, 24)}%)`,
        borderColor: `hsl(${tone.hue} ${Math.max(tone.sat - 12, 26)}% 82%)`,
      };

/** Solid color used for legend swatches / tooltips. */
export const chartColor = (tone) =>
  tone == null ? "hsl(215 14% 64%)" : `hsl(${tone.hue} ${tone.sat}% ${tone.light}%)`;

/** Soft gradient stops used for chart slices/bars (light -> deep of same tone). */
export const chartGradient = (tone) =>
  tone == null
    ? { from: "hsl(215 14% 78%)", to: "hsl(215 14% 62%)" }
    : {
        from: `hsl(${tone.hue} ${Math.max(tone.sat - 4, 30)}% ${Math.min(tone.light + 14, 74)}%)`,
        to: `hsl(${tone.hue} ${tone.sat + 4}% ${Math.max(tone.light - 6, 36)}%)`,
      };

export const stateBadgeStyle = (code, overrides) => {
  const hex = overrides?.[(code || "").toUpperCase()];
  return badgeStyle(hex ? hexToTone(hex) : stateTone(code));
};
export const specialtyBadgeStyle = (name, overrides) => {
  const hex = overrides?.[(name || "").trim().toLowerCase()];
  return badgeStyle(hex ? hexToTone(hex) : specialtyTone(name));
};
export const stateChartColor = (code, overrides) => {
  const hex = overrides?.[(code || "").toUpperCase()];
  return hex || chartColor(stateTone(code));
};
export const specialtyChartColor = (name, overrides) => {
  const hex = overrides?.[(name || "").trim().toLowerCase()];
  return hex || chartColor(specialtyTone(name));
};
export const stateChartGradient = (code, overrides) => {
  const hex = overrides?.[(code || "").toUpperCase()];
  return chartGradient(hex ? hexToTone(hex) : stateTone(code));
};
export const specialtyChartGradient = (name, overrides) => {
  const hex = overrides?.[(name || "").trim().toLowerCase()];
  return chartGradient(hex ? hexToTone(hex) : specialtyTone(name));
};
export const neutralChartGradient = () => chartGradient(null);

// ---------------------------------------------------------------------------
// Hex helpers (Control Panel color overrides)
// ---------------------------------------------------------------------------

export const DEFAULT_STATUS_HEX = { Active: "#059669", Inactive: "#dc2626", Pending: "#2563eb" };

export const hexToTone = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { hue: Math.round(h * 360), sat: Math.round(s * 100), light: Math.round(l * 100) };
};

const hslChannel = (p, q, t) => {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
};

export const toneToHex = (tone) => {
  if (tone == null) return "#94a3b8";
  const h = tone.hue / 360;
  const s = tone.sat / 100;
  const l = tone.light / 100;
  let r;
  let g;
  let b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hslChannel(p, q, h + 1 / 3);
    g = hslChannel(p, q, h);
    b = hslChannel(p, q, h - 1 / 3);
  }
  const to2 = (v) => Math.round(v * 255).toString(16).padStart(2, "0");
  return `#${to2(r)}${to2(g)}${to2(b)}`;
};

export const defaultStateHex = (code) => toneToHex(stateTone(code));
export const defaultSpecialtyHex = (name) => toneToHex(specialtyTone(name));

/** Soft row-tint style for a status color (used by Business Tracker rows). */
export const statusRowStyle = (status, overrides) => {
  const hex = overrides?.[status] || DEFAULT_STATUS_HEX[status];
  if (!hex) return null;
  return { backgroundColor: `${hex}14` };
};

/** Badge style for a status value (override-aware, falls back to defaults). */
export const statusBadgeStyle = (status, overrides) => {
  const hex = overrides?.[status] || DEFAULT_STATUS_HEX[status];
  return badgeStyle(hex ? hexToTone(hex) : null);
};
